import { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import {
    X,
    Copy,
    Check,
    ExternalLink,
    Globe,
    CreditCard,
    Package,
    Link as LinkIcon
} from 'lucide-react';
import { generateTrackingUrl, LANGUAGES, CURRENCIES } from '../../utils/tracking';

interface AdminLinkGeneratorModalProps {
    isOpen: boolean;
    onClose: () => void;
    affiliate: {
        id: string;
        email: string;
        companyName?: string;
    } | null;
}

interface Product {
    id: string;
    name: string;
    description?: string;
    landing_page_url: string;
    landing_pages?: { id: string; name: string; url: string }[]; // Added this line
    creatives?: {
        landing_pages?: { id: string; name: string; url: string }[];
    };
}

export default function AdminLinkGeneratorModal({ isOpen, onClose, affiliate }: AdminLinkGeneratorModalProps) {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    // Selection state
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [selectedLandingPage, setSelectedLandingPage] = useState<string>('default');
    const [clickIdParam, setClickIdParam] = useState('');
    const [subIdParam, setSubIdParam] = useState('');
    const [sourceIdParam, setSourceIdParam] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchProducts();
        }
    }, [isOpen]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const productsQuery = query(
                collection(db, 'products'),
                where('active', '==', true)
            );
            const snapshot = await getDocs(productsQuery);
            const productsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Product[];
            setProducts(productsList);
            if (productsList.length > 0) {
                setSelectedProduct(productsList[0]);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    // Combine top-level landing pages and legacy creative landing pages
    const availableLandingPages = [
        ...(selectedProduct?.landing_pages || []),
        ...(selectedProduct?.creatives?.landing_pages || [])
    ];

    const trackingUrl = selectedProduct && affiliate ? generateTrackingUrl({
        baseUrl: selectedProduct.landing_page_url,
        affiliateId: affiliate.id,
        language: selectedLanguage,
        currency: selectedCurrency,
        productId: selectedProduct.id,
        landingPageId: selectedLandingPage,
        creatives: selectedProduct.creatives,
        landing_pages: selectedProduct.landing_pages,
        clickId: clickIdParam,
        subId: subIdParam,
        sourceId: sourceIdParam
    }) : '';

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(trackingUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    if (!isOpen || !affiliate) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="glass-dark rounded-2xl p-6 w-full max-w-2xl animate-slide-up max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
                            <LinkIcon className="text-primary-400" size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Generate Tracking Link</h2>
                            <p className="text-sm text-gray-400">For {affiliate.companyName || affiliate.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white"
                    >
                        <X size={24} />
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Product Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                <Package size={16} className="inline mr-2" />
                                Product
                            </label>
                            <select
                                value={selectedProduct?.id || ''}
                                onChange={(e) => {
                                    const prod = products.find(p => p.id === e.target.value);
                                    setSelectedProduct(prod || null);
                                    setSelectedLandingPage('default');
                                }}
                                className="input-field"
                            >
                                {products.map(product => (
                                    <option key={product.id} value={product.id}>
                                        {product.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Language, Currency & Entry Link */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <Globe size={16} className="inline mr-2" />
                                    Language
                                </label>
                                <select
                                    value={selectedLanguage}
                                    onChange={(e) => setSelectedLanguage(e.target.value)}
                                    className="input-field"
                                >
                                    {LANGUAGES.map(lang => (
                                        <option key={lang.code} value={lang.code}>
                                            {lang.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    <CreditCard size={16} className="inline mr-2" />
                                    Currency
                                </label>
                                <select
                                    value={selectedCurrency}
                                    onChange={(e) => setSelectedCurrency(e.target.value)}
                                    className="input-field"
                                >
                                    {CURRENCIES.map(curr => (
                                        <option key={curr.code} value={curr.code}>
                                            {curr.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                {availableLandingPages.length > 0 && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            <ExternalLink size={16} className="inline mr-2" />
                                            Entry Link
                                        </label>
                                        <select
                                            value={selectedLandingPage}
                                            onChange={(e) => setSelectedLandingPage(e.target.value)}
                                            className="input-field"
                                        >
                                            <option value="default">Default Product Page</option>
                                            {availableLandingPages.map(lp => (
                                                <option key={lp.id} value={lp.id}>
                                                    {lp.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* URL Parameters */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Click ID
                                </label>
                                <input
                                    type="text"
                                    value={clickIdParam}
                                    onChange={e => setClickIdParam(e.target.value)}
                                    placeholder="{click.id}"
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Sub ID
                                </label>
                                <input
                                    type="text"
                                    value={subIdParam}
                                    onChange={e => setSubIdParam(e.target.value)}
                                    placeholder="{sub.id}"
                                    className="input-field"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Source ID
                                </label>
                                <input
                                    type="text"
                                    value={sourceIdParam}
                                    onChange={e => setSourceIdParam(e.target.value)}
                                    placeholder="{source.id}"
                                    className="input-field"
                                />
                            </div>
                        </div>

                        {/* Generated URL */}
                        <div className="p-4 bg-primary-500/5 rounded-xl border border-primary-500/20">
                            <label className="block text-sm font-medium text-primary-300 mb-2 font-semibold">
                                Tracking Link
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={trackingUrl}
                                    readOnly
                                    className="input-field flex-1 text-sm font-mono bg-gray-900/50"
                                />
                                <button
                                    onClick={handleCopy}
                                    className="btn-primary px-4 flex items-center gap-2"
                                >
                                    {copied ? (
                                        <Check size={18} />
                                    ) : (
                                        <Copy size={18} />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                onClick={onClose}
                                className="btn-secondary flex-1"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
